import * as Api from '@ViewModels';
import * as React from 'react';
import { AppState } from '../../models/AppState';
import { IRealtimeTranscriptionUtteranceEvent } from '../../viewModels/phonecall';
import { IWebsocketSubscription } from '../../viewModels/websockets';
import { useTysonCoaching } from '../contexts/TysonCoachingContext';
import { TranscriptEntry } from '../types';

interface UseRealCallCoachingProps {
	phoneCallId: string | null;
	enabled: boolean;
}

/**
 * Hook that bridges real-time Twilio transcription events to the Tyson coaching system.
 * Subscribes to RealtimeTranscriptionUtteranceEvent WebSocket events and forwards
 * transcript entries to the coaching context.
 *
 * When a Lead utterance is finalized, we send all pending entries immediately to minimize latency.
 * The coaching context handles queuing if the AI is busy responding.
 */
export const useRealCallCoaching = ({ phoneCallId, enabled }: UseRealCallCoachingProps) => {
	const coaching = useTysonCoaching();
	const { websockets } = AppState;
	const [transcriptCount, setTranscriptCount] = React.useState(0);

	// Track pending entries until Lead speaks
	const pendingEntriesRef = React.useRef<TranscriptEntry[]>([]);

	React.useEffect(() => {
		if (!enabled || !phoneCallId || !coaching.isActive) {
			return;
		}

		const handleTranscriptionEvent = (events: Api.IRemoteEvent<IRealtimeTranscriptionUtteranceEvent>[]) => {
			for (const event of events) {
				const utterance = event.value;

				// Only process events for the current call
				if (utterance.phoneCallId !== phoneCallId) {
					continue;
				}

				// Only process final utterances to avoid duplicates
				if (!utterance.final) {
					continue;
				}

				// Convert to TranscriptEntry format
				const entry: TranscriptEntry = {
					speaker: utterance.speaker === 'inbound_track' ? 'Lead' : 'Sales Rep',
					message: utterance.transcription,
					timestamp: new Date(),
				};

				// Add entry to pending
				pendingEntriesRef.current = [...pendingEntriesRef.current, entry];

				// When Lead speaks, send all pending entries immediately for fastest coaching
				if (entry.speaker === 'Lead') {
					pendingEntriesRef.current.forEach(e => coaching.sendTranscriptEntry(e));
					pendingEntriesRef.current = [];
				}

				setTranscriptCount(prev => prev + 1);
			}
		};

		const subscription: IWebsocketSubscription<IRealtimeTranscriptionUtteranceEvent> = {
			callback: handleTranscriptionEvent,
			events: ['RealtimeTranscriptionUtteranceEvent'],
			name: 'realCallCoaching',
		};

		websockets.subscribe(subscription);

		return () => {
			websockets.unsubscribe('realCallCoaching');
		};
	}, [enabled, phoneCallId, coaching, websockets]);

	// Reset refs when call ends or hook disables
	React.useEffect(() => {
		if (!enabled) {
			pendingEntriesRef.current = [];
		}
	}, [enabled]);

	return {
		transcriptCount,
		isActive: enabled && !!phoneCallId && coaching.isActive,
	};
};
